import React, { type ReactNode, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { Modal } from '@systemeio/components'
import { XCloseIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { LikeButton } from 'shared/components/like/like-button'
import { useDocumentScrollLock } from 'shared/hooks/use-document-scroll-lock'
import useDomainAssets from 'shared/hooks/use-domain-assets'
import useUser from 'shared/hooks/use-user'
import { PostAttachmentsProvider } from 'modules/attachments/components/context'
import { Comment } from 'modules/comments/components/comment'
import { CommentsContext } from 'modules/comments/components/comments-list'
import type { CommentsContextType } from 'modules/comments/components/comments-list/comments-list.types'
import { CommentHighlightProvider } from 'modules/comments/contexts/comment-highlight'
import { CommentSortOptions, CommentsTypeEnum } from 'modules/comments/types'
import { PostView } from 'modules/community/components/post/components/post-view'
import { shouldHideUnderModerationContent } from 'modules/community/components/under-moderation-placeholder'
import { PostStatusEnum } from 'modules/community/enums/post-status-enum'
import type {
  PostCommentInfoResult,
  PostCommentsInfoInterface,
} from 'modules/community/types/post-info'
import type {
  CommentsSearchInterface,
  PostInterface,
  SearchPostInterface,
} from 'modules/community/types/post-interface'
import { isSearchPostGuard } from 'modules/community/types/post-interface'
import { useBrand } from 'modules/white-label/contexts/brand-context'
import { PostPinnedActions } from '../pinned-post/components/post-pinned-actions'
import { PostActionsMenu } from '../post/components/post-actions-menu'
import { PostAuthorInfo } from './author-info'
import PostCommentsInfo from './comments-info'
import { PostAnnounceLayout } from './components/post-announce-layout'

interface PostAnnounceProps<T extends PostInterface | SearchPostInterface> {
  post: T
  deletePost: () => Promise<void>
  deletePostAndBanUser: (removeRecentActivitiesChecked: boolean) => Promise<void>
  handleLikeClick?: () => void
  isPinnedPost?: boolean
  comments?: CommentsSearchInterface[]
  commentsInfo?: PostCommentsInfoInterface[]
  commentsInfoError?: Error | null
}

function PostModalMobileHeader({
  post,
  hideContent,
  onClose,
}: {
  post: PostInterface | SearchPostInterface
  hideContent: boolean
  onClose: () => void
}): ReactNode {
  const { t } = useTranslation()
  const { domainAssets } = useDomainAssets()
  const { brand } = useBrand()

  return (
    <div className="sticky top-0 z-100 flex h-12 w-full items-center border-b border-text-secondary/30 bg-bg-primary px-4 py-2 md:hidden">
      <div className="flex w-full min-w-0">
        <div className="flex min-w-0 items-center gap-2 text-text-primary">
          <button type="button" className="flex items-center justify-center" onClick={onClose}>
            <XCloseIcon className="size-5 fill-text-secondary" />
          </button>
          <img
            src={domainAssets?.logoUrl || brand.logo?.path}
            alt="logo"
            className="h-6 w-auto shrink-0"
            width={24}
            height={24}
          />
          <p className="truncate text-sm font-bold">
            {hideContent ? t('community.moderation.content.post_hidden') : post.title}
          </p>
        </div>
      </div>
    </div>
  )
}

function PostAnnounce<T extends PostInterface | SearchPostInterface>({
  post,
  deletePost,
  handleLikeClick,
  deletePostAndBanUser,
  isPinnedPost,
  comments,
  commentsInfo,
  commentsInfoError,
}: PostAnnounceProps<T>): ReactNode {
  const router = useRouter()
  const { user } = useUser()
  const { communityPath, topicPath: topicPathParam } = router.query
  const communityPathWithSlug = `/community/${communityPath}`
  const topicPath = `/community/${communityPath}/${post.topic.path}`
  const postDetailsPath = `${topicPath}/${post.path}`
  const [opened, setOpened] = useState(false)
  useDocumentScrollLock(Boolean(opened && user))
  const hidePostContent = shouldHideUnderModerationContent(post.underModeration, user)
  const [commentsHighlightContext, setCommentsHighlightContext] = useState<{
    targetCommentId?: number
    searchHighlightedComments?: CommentsSearchInterface[]
  }>()

  const isSearchPost = isSearchPostGuard(post)

  const handleModalOpen = (e: React.MouseEvent<HTMLDivElement>): void => {
    const isLink = e.target instanceof HTMLElement && e.target.tagName.toLowerCase() === 'a'

    if (!isLink) {
      if (globalThis) {
        globalThis.history.replaceState(null, '', postDetailsPath)
      }
      setOpened(true)
    }
  }

  const handleModalClose = (): void => {
    if (globalThis) {
      globalThis.history.replaceState(null, '', topicPathParam ? topicPath : communityPathWithSlug)
    }
    setOpened(false)
    setCommentsHighlightContext(undefined)
  }

  const handleCommentClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation()
    const { commentId } = e.currentTarget.dataset
    const numericalCommentId = Number.parseInt(commentId ?? '0', 10)
    if (numericalCommentId && !Number.isNaN(numericalCommentId)) {
      setCommentsHighlightContext({
        targetCommentId: numericalCommentId,
        searchHighlightedComments: comments,
      })
    }
    handleModalOpen(e)
  }

  const loadedCommentsInfo = commentsInfo?.find(item => item.postId === post.id)
  const postCommentsInfo: PostCommentInfoResult | undefined =
    (loadedCommentsInfo && { ok: true, value: loadedCommentsInfo }) ||
    (commentsInfoError && { ok: false, error: commentsInfoError }) ||
    (commentsInfo && { ok: false }) ||
    undefined

  if (post.status !== PostStatusEnum.Pending && !post.publishedAt) {
    Sentry.captureMessage('Regular post has no publishedAt', {
      level: 'error',
      extra: { post },
    })
    return null
  }

  if (!communityPath) return null

  const searchCommentsContext: CommentsContextType = {
    commentsType: CommentsTypeEnum.Community,
    commentsList: [],
    setComments: () => {},
    repliesList: {},
    updateReplies: () => {},
    pinnedComments: [],
    setPinnedComments: () => {},
    readOnly: true,
    extra: { community: { communityPath, postId: post.id } },
    currentSortOption: CommentSortOptions.NEWEST_FIRST,
  }

  return (
    <PostAnnounceLayout
      postData={post}
      isPinnedPost={isPinnedPost}
      isNeedTruncate={true}
      onClick={handleModalOpen}
      headerTitle={
        <PostAuthorInfo
          post={post}
          highlightedAuthorHTML={isSearchPost ? post.highlight?.author : undefined}
        />
      }
      headerActions={
        <>
          <PostPinnedActions post={post} />
          <PostActionsMenu
            post={post}
            deletePost={deletePost}
            deletePostAndBanUser={deletePostAndBanUser}
          />
        </>
      }
      footerContent={
        <div className="flex flex-col gap-2">
          {post.status === PostStatusEnum.Approved && (
            <div className="mt-[7px] flex items-center gap-[2px]">
              {handleLikeClick && (
                <div
                  onClick={e => {
                    e.stopPropagation()
                  }}
                >
                  <LikeButton
                    isLiked={post.isLiked}
                    likesCount={post.likesCount}
                    handleLikeClick={handleLikeClick}
                    postId={post.id}
                    readOnly={hidePostContent}
                  />
                </div>
              )}
              <PostCommentsInfo info={postCommentsInfo} />
            </div>
          )}
          <CommentsContext.Provider value={searchCommentsContext}>
            <CommentHighlightProvider>
              {comments?.map(comment => (
                <div
                  key={comment.id}
                  data-comment-id={comment.id}
                  role="button"
                  onClick={handleCommentClick}
                >
                  <Comment data={comment} level={0} replies={comment.children} />
                </div>
              ))}
            </CommentHighlightProvider>
          </CommentsContext.Provider>
        </div>
      }
      extraContent={
        opened &&
        user && (
          <Modal.Root
            open
            disablePointerDismissal
            onOpenChange={(open, eventDetails) => {
              if (!open && eventDetails.reason === 'escape-key') return
              if (!open) handleModalClose()
            }}
          >
            <PostAttachmentsProvider attachments={post.attachments}>
              <Modal.Popup
                showCloseButton={false}
                backdropProps={{ onClick: handleModalClose }}
                viewportProps={{
                  className: 'block overflow-y-auto p-0 md:flex md:flex-col md:pt-16',
                  render: (
                    <div data-scrollable="comments-container" data-testid="comments-container" />
                  ),
                  onClick: e => {
                    if (e.target === e.currentTarget) handleModalClose()
                  },
                }}
                className="mx-auto max-h-none min-h-screen w-full max-w-200 overflow-visible text-left md:my-auto md:min-h-fit"
              >
                <Modal.Close
                  render={
                    <button
                      type="button"
                      className="absolute top-0 -right-15 z-1001 flex items-center justify-center max-md:hidden"
                    />
                  }
                >
                  <XCloseIcon className="size-11 [--icon-color-primary:var(--color-icon-on)]" />
                </Modal.Close>
                <PostModalMobileHeader
                  post={post}
                  hideContent={hidePostContent}
                  onClose={handleModalClose}
                />
                <PostView
                  post={post}
                  handleDeletePost={deletePost}
                  handleLikeClick={handleLikeClick}
                  deletePostAndBanUser={deletePostAndBanUser}
                  isLoading={false}
                  commentsHighlightContext={commentsHighlightContext}
                />
              </Modal.Popup>
            </PostAttachmentsProvider>
          </Modal.Root>
        )
      }
    />
  )
}

export default PostAnnounce
